// src/components/nav/AdminHeader.jsx
import React from "react";
import { createPortal } from "react-dom";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Activity,
  Bell,
  ChevronDown,
  LifeBuoy,
  LayoutDashboard,
  LogOut,
  Menu,
  Monitor,
  Moon,
  MessageSquareWarning,
  ShieldCheck,
  Sun,
  Upload,
  UserRound,
  UsersRound,
  X,
} from "lucide-react";

import AfrimedSearchBar from "@/components/ui/AfrimedSearchBar.jsx";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import { useTheme } from "@/contexts/ThemeContext.jsx";
import { logout } from "@/lib/auth/logout";
import AvatarThumb68 from "@/sot/primitives/AvatarThumb68.jsx";
import {
  ADMIN_FEEDBACK_READ_EVENT,
  countUnreadFeedbackReports,
  extractFeedbackIdFromNotification,
  listAdminHeaderNotifications,
  markFeedbackReportsRead,
} from "@/services/adminFeedbackService.js";
import {
  ADMIN_PROFILE_UPDATED_EVENT,
  getAdminProfile,
  getAdminProfilePhotoUrl,
  updateAdminThemePreference,
} from "@/services/adminProfileService.js";
import supabase from "@/services/supabaseClient";

const BRAND_LOGO = "/assets/images/afrimed_logo.svg";
const FALLBACK_AVATAR = "/icons/circular-avatar-placeholder.svg";
const WELLNESS_BADGE_BG = "bg-[#00BDC4]";
const WELLNESS_BADGE_SOFT = "bg-[rgba(0,189,196,0.12)]";
const WELLNESS_BADGE_TEXT = "text-[#008A8F]";

const THEME_OPTIONS = [
  { value: "dark", labelKey: "admin.theme.option.dark", fallbackLabel: "Dark", icon: Moon },
  { value: "light", labelKey: "admin.theme.option.light", fallbackLabel: "Light", icon: Sun },
  { value: "system", labelKey: "admin.theme.option.system", fallbackLabel: "System", icon: Monitor },
];

function clsChip(active) {
  return [
    "h-10 px-2 lg:px-3 flex items-center justify-center text-body-medium border-b-2 transition",
    active
      ? "text-on-surface border-[rgba(var(--clr-on-surface),0.88)]"
      : "border-transparent text-on-surface-var hover:text-on-surface",
  ].join(" ");
}

function MenuItem({ label, icon: Icon, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 px-4 py-3 text-left text-body-medium text-on-surface hover:bg-[rgba(var(--clr-on-surface),0.06)]"
    >
      <Icon className="h-5 w-5 text-on-surface-var" aria-hidden />
      <span className="flex-1">{label}</span>
    </button>
  );
}

function MobileNavButton({ label, icon: Icon, active = false, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "flex w-full items-center gap-3 rounded-[12px] px-4 py-3 text-left transition",
        active
          ? "bg-[rgba(var(--afrimed-primary),0.10)] text-on-surface"
          : "text-on-surface hover:bg-[rgba(var(--clr-on-surface),0.06)]",
      ].join(" ")}
    >
      <Icon className="h-5 w-5 shrink-0 text-on-surface-var" aria-hidden />
      <span className="flex-1 text-body-large">{label}</span>
    </button>
  );
}

function resolveAdminSearchTarget(pathname = "") {
  const path = String(pathname || "").trim();
  if (path === "/admin/kyc") return "/admin/kyc";
  if (path === "/admin/support") return "/admin/support";
  if (path === "/admin/users") return "/admin/users";
  if (path === "/admin/feedback") return "/admin/feedback";
  return "/admin/kpi";
}

function resolveAvatar(auth = {}) {
  const profile = auth?.profile || {};
  const userMeta = auth?.user?.user_metadata || {};
  return (
    profile?.profile_image_url ||
    profile?.avatar_url ||
    profile?.photo_url ||
    userMeta?.avatar_url ||
    userMeta?.picture ||
    FALLBACK_AVATAR
  );
}

function resolveAdminName(auth = {}, adminProfile = null, fallbackEmail = "") {
  const profile = auth?.profile || {};
  const userMeta = auth?.user?.user_metadata || {};
  const preferred =
    adminProfile?.full_name ||
    profile?.full_name ||
    [profile?.first_name, profile?.last_name].filter(Boolean).join(" ").trim() ||
    profile?.display_name ||
    userMeta?.full_name ||
    userMeta?.name ||
    userMeta?.display_name ||
    String(fallbackEmail || "").split("@")[0];

  return String(preferred || "Admin").trim() || "Admin";
}

function normalizeNotifications(items) {
  return Array.isArray(items) ? items : [];
}

function ThemeOptionButton({ active, label, icon: Icon, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "flex w-full items-center gap-3 rounded-[12px] px-3 py-2 text-left text-body-medium transition",
        active
          ? "bg-[rgba(var(--afrimed-primary),0.10)] text-on-surface"
          : "text-on-surface hover:bg-[rgba(var(--clr-on-surface),0.06)]",
      ].join(" ")}
    >
      <Icon className="h-[18px] w-[18px] text-on-surface-var" aria-hidden />
      <span className="flex-1">{label}</span>
      <span
        className={[
          "h-2.5 w-2.5 rounded-full border",
          active
            ? "border-[rgb(var(--afrimed-primary))] bg-[rgb(var(--afrimed-primary))]"
            : "border-[rgba(var(--clr-outline),0.9)] bg-transparent",
        ].join(" ")}
      />
    </button>
  );
}

function AdminMePanel({
  t,
  fullName,
  email,
  loggingOut,
  onNavigateAccount,
  onNavigateKpi,
  onNavigateKyc,
  onNavigateTeam,
  onOpenLogout,
  canManageTeam,
  canViewKpi,
  canViewKyc,
  theme,
  onSelectTheme,
}) {
  const displayName = String(email || "admin").split("@")[0] || "admin";

  return (
    <div className="absolute right-0 top-[calc(100%+8px)] z-[60] w-[300px] overflow-hidden rounded-[12px] border border-[rgba(var(--clr-outline-variant),0.55)] bg-[rgb(var(--clr-surface-cont-highest))] shadow-[0px_12px_24px_-10px_rgba(29,79,215,0.10),0px_6px_12px_-4px_rgba(26,27,35,0.08)]">
      <div className="border-b border-[rgba(var(--clr-outline-variant),0.55)] px-4 py-4">
        <div className="min-w-0">
          <p className="truncate text-title-medium text-on-surface">{fullName || displayName}</p>
          <div className="mt-1 flex items-center justify-between gap-3">
            <p className="min-w-0 flex-1 truncate text-body-small text-on-surface-var">
              {email || "No admin email"}
            </p>
            <button
              type="button"
              onClick={onNavigateAccount}
              className="inline-flex shrink-0 items-center gap-2 text-label-medium text-on-surface-var transition hover:text-on-surface"
            >
              <Upload className="h-5 w-5 text-on-surface-var" aria-hidden />
              <span>{t("admin.menu.uploadImage", "Upload image")}</span>
            </button>
          </div>
        </div>
      </div>

      <div className="px-2 py-2">
        <MenuItem label={t("admin.menu.myAccount", "My account")} icon={UserRound} onClick={onNavigateAccount} />
        {canViewKpi ? (
          <MenuItem label={t("admin.menu.kpiDashboard", "KPI dashboard")} icon={LayoutDashboard} onClick={onNavigateKpi} />
        ) : null}
        {canViewKyc ? (
          <MenuItem label={t("admin.menu.kycReview", "KYC review")} icon={ShieldCheck} onClick={onNavigateKyc} />
        ) : null}
        {canManageTeam ? (
          <MenuItem label={t("admin.menu.adminTeam", "Admin team")} icon={UsersRound} onClick={onNavigateTeam} />
        ) : null}
      </div>

      <div className="border-y border-[rgba(var(--clr-outline-variant),0.55)] px-4 py-3">
        <p className="text-body-medium text-on-surface-var">{t("admin.theme.title", "Theme")}</p>
        <div className="mt-2 space-y-1">
          {THEME_OPTIONS.map((option) => (
            <ThemeOptionButton
              key={option.value}
              active={theme === option.value}
              label={t(option.labelKey, option.fallbackLabel)}
              icon={option.icon}
              onClick={() => onSelectTheme(option.value)}
            />
          ))}
        </div>
      </div>

      <div className="px-2 py-2">
        <MenuItem
          label={loggingOut ? t("admin.menu.signingOut", "Signing out...") : t("admin.menu.signOut", "Sign out")}
          icon={LogOut}
          onClick={onOpenLogout}
        />
      </div>
    </div>
  );
}

export default function AdminHeader({ section = "kpi", notifications = [], hideMobileSearch = false }) {
  const nav = useNavigate();
  const location = useLocation();
  const auth = useAuth() || {};
  const { t } = useLanguage();
  const { theme, setTheme } = useTheme();
  const [open, setOpen] = React.useState(false);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [notificationsOpen, setNotificationsOpen] = React.useState(false);
  const [loggingOut, setLoggingOut] = React.useState(false);
  const [logoutOpen, setLogoutOpen] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState("");
  const [avatarFailed, setAvatarFailed] = React.useState(false);
  const [adminProfile, setAdminProfile] = React.useState(null);
  const [adminProfilePhotoUrl, setAdminProfilePhotoUrl] = React.useState("");
  const [liveNotifications, setLiveNotifications] = React.useState([]);
  const [feedbackUnreadCount, setFeedbackUnreadCount] = React.useState(0);
  const meMenuRef = React.useRef(null);

  const email = String(auth?.user?.email || "").trim();
  const userId = String(auth?.user?.id || "").trim();
  const fullName = resolveAdminName(auth, adminProfile, email);
  const avatarSrc = avatarFailed
    ? FALLBACK_AVATAR
    : adminProfilePhotoUrl || resolveAvatar(auth);
  const allNotifications = normalizeNotifications(
    Array.isArray(notifications) && notifications.length > 0
      ? notifications
      : liveNotifications,
  );
  const unreadNotifications = feedbackUnreadCount;
  const adminRole = String(auth?.profile?.role || auth?.role || "").trim().toLowerCase();
  const isSuperAdmin = adminRole === "super_admin";
  const canViewKpi = adminRole === "super_admin" || adminRole === "admin";
  const canViewUsers = adminRole === "super_admin" || adminRole === "admin" || adminRole === "support";
  const canViewKyc = adminRole === "super_admin" || adminRole === "admin" || adminRole === "kyc_reviewer";
  const canViewFeedback = adminRole === "super_admin" || adminRole === "admin" || adminRole === "support";
  const canViewSupport = adminRole === "super_admin" || adminRole === "admin" || adminRole === "support";

  const navItems = [
    canViewKpi ? { key: "kpi", to: "/admin/kpi", label: t("admin.nav.kpi", "KPI"), icon: LayoutDashboard } : null,
    canViewUsers ? { key: "users", to: "/admin/users", label: t("admin.nav.users", "Users"), icon: UsersRound } : null,
    canViewKyc ? { key: "kyc", to: "/admin/kyc", label: t("admin.nav.kycReview", "KYC Review"), icon: ShieldCheck } : null,
    canViewFeedback ? { key: "feedback", to: "/admin/feedback", label: t("admin.nav.feedback", "Feedback"), icon: MessageSquareWarning } : null,
    canViewSupport ? { key: "support", to: "/admin/support", label: t("admin.nav.support", "Support"), icon: LifeBuoy } : null,
  ].filter(Boolean);

  React.useEffect(() => {
    if (!userId) return undefined;
    let alive = true;
    async function loadNotifications() {
      try {
        const data = await listAdminHeaderNotifications({ userId, limit: 50 });
        if (!alive) return;
        setLiveNotifications(Array.isArray(data) ? data : []);
      } catch {
        if (!alive) return;
        setLiveNotifications([]);
      }
    }
    void loadNotifications();
    function handleFeedbackReadChanged() {
      void loadNotifications();
    }
    window.addEventListener(ADMIN_FEEDBACK_READ_EVENT, handleFeedbackReadChanged);
    const timer = window.setInterval(loadNotifications, 15000);
    return () => {
      alive = false;
      window.removeEventListener(ADMIN_FEEDBACK_READ_EVENT, handleFeedbackReadChanged);
      window.clearInterval(timer);
    };
  }, [userId]);

  React.useEffect(() => {
    if (!userId) {
      setFeedbackUnreadCount(0);
      return undefined;
    }

    let alive = true;

    async function loadUnreadFeedback() {
      try {
        const count = await countUnreadFeedbackReports();
        if (!alive) return;
        setFeedbackUnreadCount(count);
      } catch {
        if (!alive) return;
        setFeedbackUnreadCount(0);
      }
    }

    void loadUnreadFeedback();

    function handleFeedbackReadChanged() {
      void loadUnreadFeedback();
    }

    window.addEventListener(ADMIN_FEEDBACK_READ_EVENT, handleFeedbackReadChanged);
    const timer = window.setInterval(loadUnreadFeedback, 15000);
    return () => {
      alive = false;
      window.removeEventListener(ADMIN_FEEDBACK_READ_EVENT, handleFeedbackReadChanged);
      window.clearInterval(timer);
    };
  }, [userId]);

  React.useEffect(() => {
    setOpen(false);
    setMobileOpen(false);
    setNotificationsOpen(false);
  }, [section, location.pathname]);

  React.useEffect(() => {
    if (!open || typeof document === "undefined") return undefined;

    function handlePointerDown(event) {
      const target = event.target;
      if (meMenuRef.current && target instanceof Node && !meMenuRef.current.contains(target)) {
        setOpen(false);
      }
    }

    function handleKeyDown(event) {
      if (event.key === "Escape") setOpen(false);
    }

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    setSearchValue(String(params.get("q") || "").trim());
  }, [location.search]);

  React.useEffect(() => {
    if (!mobileOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mobileOpen]);

  React.useEffect(() => {
    if (!notificationsOpen) return undefined;
    if (typeof window !== "undefined" && window.innerWidth >= 768) {
      return undefined;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [notificationsOpen]);

  React.useEffect(() => {
    if (!userId) return undefined;

    let alive = true;

    async function loadAdminProfile() {
      try {
        const nextProfile = await getAdminProfile({
          userId,
          email,
          fullName:
            String(auth?.profile?.full_name || "").trim() ||
            String(auth?.profile?.display_name || "").trim(),
        });
        if (!alive) return;
        setAdminProfile(nextProfile);

        const photoPath = String(nextProfile?.profile_photo_path || "").trim();
        if (!photoPath) {
          setAdminProfilePhotoUrl("");
          setAvatarFailed(false);
          return;
        }

        const signedUrl = await getAdminProfilePhotoUrl(photoPath);
        if (!alive) return;
        setAdminProfilePhotoUrl(signedUrl);
        setAvatarFailed(false);
      } catch {
        if (!alive) return;
        setAdminProfile(null);
        setAdminProfilePhotoUrl("");
      }
    }

    loadAdminProfile();

    function handleProfileUpdated() {
      loadAdminProfile();
    }

    window.addEventListener(ADMIN_PROFILE_UPDATED_EVENT, handleProfileUpdated);
    return () => {
      alive = false;
      window.removeEventListener(ADMIN_PROFILE_UPDATED_EVENT, handleProfileUpdated);
    };
  }, [auth?.profile?.display_name, auth?.profile?.full_name, auth?.user?.id, email]);

  React.useEffect(() => {
    const persistedTheme = String(adminProfile?.theme_preference || "").trim().toLowerCase();
    if (!persistedTheme || persistedTheme === theme) return;
    setTheme(persistedTheme);
  }, [adminProfile?.theme_preference, setTheme, theme]);

  async function handleLogout() {
    if (loggingOut) return;
    setLoggingOut(true);
    await logout({ redirectTo: "/admin" });
  }

  function handleMobileNavigate(to) {
    setMobileOpen(false);
    nav(to);
  }

  function handleOpenNotification(item) {
    if (!item) return;
    const notificationId = String(item?.id || "").trim();
    const feedbackId = extractFeedbackIdFromNotification(item);
    if (feedbackId) {
      void markFeedbackReportsRead({
        ids: [feedbackId],
        userId,
      })
        .then(() => {
          setLiveNotifications((current) =>
            current.filter((row) => String(row?.id || "").trim() !== notificationId),
          );
        })
        .catch(() => {
          // keep navigation responsive even if read sync fails
        });
    } else if (notificationId && userId) {
      void supabase
        .from("notifications")
        .update({ read_at: new Date().toISOString() })
        .eq("user_id", userId)
        .eq("id", notificationId)
        .is("read_at", null)
        .then(() => {
          setLiveNotifications((current) =>
            current.filter((row) => String(row?.id || "").trim() !== notificationId),
          );
        })
        .catch(() => {
          // keep navigation responsive even if read sync fails
        });
    }
    setNotificationsOpen(false);
    setMobileOpen(false);
    nav(item.route || "/admin/kpi");
  }

  function submitSearch(event) {
    event?.preventDefault?.();
    const targetPath = resolveAdminSearchTarget(location.pathname);
    const params = new URLSearchParams();
    const nextQuery = String(searchValue || "").trim();
    if (nextQuery) params.set("q", nextQuery);
    nav(params.toString() ? `${targetPath}?${params.toString()}` : targetPath);
    setMobileOpen(false);
  }

  async function handleThemeSelect(nextTheme) {
    setTheme(nextTheme);

    if (!userId) return;
    try {
      const nextProfile = await updateAdminThemePreference({
        userId,
        themePreference: nextTheme,
      });
      setAdminProfile((current) => ({ ...(current || {}), ...(nextProfile || {}) }));
    } catch {
      // keep local admin theme responsive even if persistence fails
    }
  }

  return (
    <>
      <div className="sticky top-0 z-40 w-full border-b border-[rgba(var(--clr-outline-variant),0.55)] bg-[rgb(var(--clr-surface))] shadow-[0_1px_0_rgba(0,0,0,0.02)]">
        <div className="md:hidden px-4 pt-3 pb-2">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              className="grid h-10 w-10 shrink-0 place-items-center rounded-[12px] hover:bg-[rgba(var(--clr-on-surface),0.06)]"
              aria-label="Open navigation"
              title="Open navigation"
            >
              <Menu className="h-5 w-5 text-on-surface" aria-hidden />
            </button>

            <button
              type="button"
              onClick={() => nav("/admin/kpi")}
              aria-label="AfriMed Admin"
              title="AfriMed Admin"
              className="inline-flex min-w-0 flex-1 items-center"
            >
              <img src={BRAND_LOGO} alt="AfriMed" className="h-7 w-auto" draggable="false" />
            </button>

            <button
              type="button"
              onClick={() => setNotificationsOpen(true)}
              className="relative grid h-10 w-10 shrink-0 place-items-center rounded-[12px] hover:bg-[rgba(var(--clr-on-surface),0.06)]"
              aria-label="Notifications"
              title="Notifications"
            >
              <Bell className="h-5 w-5 text-on-surface-var" aria-hidden />
              {unreadNotifications ? (
                <span className={`absolute right-1.5 top-1.5 min-w-[18px] rounded-full ${WELLNESS_BADGE_BG} px-1.5 py-[1px] text-[10px] font-semibold leading-4 text-white`}>
                  {Math.min(unreadNotifications, 9)}
                </span>
              ) : null}
            </button>
          </div>

          {!hideMobileSearch ? (
            <form className="mt-3" onSubmit={submitSearch} role="search" aria-label="Search">
              <button type="submit" className="sr-only">
                Search
              </button>
              <AfrimedSearchBar
                value={searchValue}
                onChange={(event) => setSearchValue(event.target.value)}
                onClear={() => setSearchValue("")}
                placeholder="Search admin"
                ariaLabel="Search"
                className="bg-[rgba(var(--clr-on-surface-variant),0.08)] border-transparent"
                showTrailing={false}
              />
            </form>
          ) : null}
        </div>

        <div className="hidden md:block">
          <div className="afrimed-rail afrimed-rail--comfortable">
            <div className="flex h-14 w-full items-center gap-4 lg:gap-6">
              <button
                type="button"
                onClick={() => nav("/admin/kpi")}
                aria-label="AfriMed Admin"
                title="AfriMed Admin"
                className="inline-flex items-center"
              >
                <img src={BRAND_LOGO} alt="AfriMed" className="h-8 w-auto" draggable="false" />
              </button>

              <div className="flex min-w-0 flex-1 items-center gap-4">
                <div className="flex min-w-0 flex-1 items-center gap-2 lg:gap-4">
                  {navItems.map((tab) => {
                    const active = section === tab.key;
                    return (
                      <NavLink key={tab.key} to={tab.to} className={() => clsChip(active)}>
                        <span>{tab.label}</span>
                      </NavLink>
                    );
                  })}
                </div>

              </div>

              <div className="relative ml-auto flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setNotificationsOpen((value) => !value)}
                  className="relative grid h-9 w-9 place-items-center rounded-xl hover:bg-[rgba(var(--clr-on-surface),0.06)]"
                  aria-label="Notifications"
                  title="Notifications"
                >
                  <Bell className="h-5 w-5 text-on-surface-var" aria-hidden />
                  {unreadNotifications ? (
                    <span className={`absolute right-0.5 top-0.5 min-w-[18px] rounded-full ${WELLNESS_BADGE_BG} px-1.5 py-[1px] text-[10px] font-semibold leading-4 text-white`}>
                      {Math.min(unreadNotifications, 9)}
                    </span>
                  ) : null}
                </button>

                <div ref={meMenuRef} className="relative">
                  <button
                    type="button"
                    onClick={() => setOpen((value) => !value)}
                    className="flex min-h-[44px] flex-col items-center justify-center rounded-[12px] px-1.5 py-1 hover:bg-[rgba(var(--clr-on-surface),0.06)]"
                    aria-label="Me menu"
                  >
                    <AvatarThumb68
                      src={avatarSrc}
                      alt="Admin avatar"
                      shape="circle"
                      role="doctor"
                      fallbackSrc={FALLBACK_AVATAR}
                      className="aspect-square"
                      style={{ width: 32, height: 32 }}
                      imgClassName={avatarFailed ? "object-contain p-[2px]" : "object-cover p-0"}
                    />

                    <div className="mt-0.5 flex items-center gap-1 text-label-small text-on-surface-var">
                      <span>Me</span>
                      <ChevronDown
                        className={`h-3.5 w-3.5 transition ${open ? "rotate-180" : ""}`}
                        aria-hidden
                      />
                    </div>
                  </button>

                  {open ? (
                    <AdminMePanel
                      t={t}
                      fullName={fullName}
                      email={email}
                      loggingOut={loggingOut}
                      onNavigateAccount={() => nav("/admin/account")}
                      onNavigateKpi={() => nav("/admin/kpi")}
                      onNavigateKyc={() => nav("/admin/kyc")}
                      onNavigateTeam={() => nav("/admin/team")}
                      onOpenLogout={() => setLogoutOpen(true)}
                      canManageTeam={isSuperAdmin}
                      canViewKpi={canViewKpi}
                      canViewKyc={canViewKyc}
                      theme={theme}
                      onSelectTheme={handleThemeSelect}
                    />
                  ) : null}
                </div>

                {notificationsOpen ? (
                  <div className="absolute right-[56px] top-[calc(100%+8px)] z-[60] hidden w-[320px] overflow-hidden rounded-[12px] border border-[rgba(var(--clr-outline-variant),0.55)] bg-[rgb(var(--clr-surface-cont-highest))] shadow-[0px_12px_24px_-10px_rgba(29,79,215,0.10),0px_6px_12px_-4px_rgba(26,27,35,0.08)] md:block">
                    <div className="border-b border-[rgba(var(--clr-outline-variant),0.55)] px-4 py-3">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-title-medium text-on-surface">Notifications</p>
                          <p className="mt-1 text-body-small text-on-surface-var">
                            Admin alerts and operational notices will appear here.
                          </p>
                        </div>
                      </div>
                    </div>
                    {allNotifications.length ? (
                      <div className="max-h-[360px] overflow-y-auto">
                        {allNotifications.map((item) => (
                          <button
                            key={item.id}
                            type="button"
                            onClick={() => handleOpenNotification(item)}
                            className="flex w-full flex-col gap-1 border-b border-[rgba(var(--clr-outline-variant),0.4)] px-4 py-3 text-left hover:bg-[rgba(var(--clr-on-surface),0.04)] last:border-b-0"
                          >
                            <div className="flex items-start gap-2">
                              <span className={`mt-1 h-2.5 w-2.5 rounded-full ${WELLNESS_BADGE_BG} opacity-90`} />
                              <div className="min-w-0 flex-1">
                                <p className="text-body-medium text-on-surface">{item.title}</p>
                                <p className="mt-1 text-body-small text-on-surface-var">{item.message}</p>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="px-4 py-5 text-body-medium text-on-surface-var">
                        No admin alerts yet.
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>

      {mobileOpen && typeof document !== "undefined"
        ? createPortal(
            <div className="md:hidden">
              <div
                className="fixed inset-0 z-[70] bg-[rgba(15,23,42,0.42)] backdrop-blur-[6px]"
                onClick={() => setMobileOpen(false)}
              />

              <aside
                className="fixed inset-y-0 left-0 z-[71] flex w-[min(86vw,320px)] flex-col overflow-y-auto border-r border-[rgba(var(--clr-outline-variant),0.55)] bg-[rgb(var(--clr-surface))] shadow-[0px_24px_48px_-20px_rgba(26,27,35,0.22)]"
                aria-label="Admin navigation"
              >
                <div className="flex items-center justify-between px-4 pt-4 pb-3">
                  <button
                    type="button"
                    onClick={() => handleMobileNavigate("/admin/kpi")}
                    aria-label="AfriMed Admin"
                    className="inline-flex items-center"
                  >
                    <img src={BRAND_LOGO} alt="AfriMed" className="h-7 w-auto" draggable="false" />
                  </button>

                  <button
                    type="button"
                    onClick={() => setMobileOpen(false)}
                    className="grid h-10 w-10 place-items-center rounded-[12px] hover:bg-[rgba(var(--clr-on-surface),0.06)]"
                    aria-label="Close navigation"
                  >
                    <X className="h-4 w-4 text-on-surface-var" aria-hidden />
                  </button>
                </div>

                {!hideMobileSearch ? (
                  <div className="border-y border-[rgba(var(--clr-outline-variant),0.4)] px-4 py-3">
                    <form onSubmit={submitSearch} role="search" aria-label="Search">
                      <button type="submit" className="sr-only">
                        Search
                      </button>
                      <AfrimedSearchBar
                        value={searchValue}
                        onChange={(event) => setSearchValue(event.target.value)}
                        onClear={() => setSearchValue("")}
                        placeholder="Search KPI, KYC, users"
                        ariaLabel="Search"
                        className="bg-[rgba(var(--clr-on-surface-variant),0.08)] border-transparent"
                        showTrailing={false}
                      />
                    </form>
                  </div>
                ) : null}

                <div className="flex-1 px-3 py-4">
                  <div className="mb-4 rounded-[12px] border border-[rgba(var(--clr-outline-variant),0.4)] bg-[rgb(var(--clr-surface-cont-highest))] p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-title-small text-on-surface">Notifications</p>
                        <p className="mt-1 text-body-small text-on-surface-var">
                          Admin alerts and operational notices will appear here.
                        </p>
                      </div>
                      {unreadNotifications ? (
                        <span className={`rounded-full ${WELLNESS_BADGE_SOFT} px-2 py-1 text-label-small ${WELLNESS_BADGE_TEXT}`}>
                          {unreadNotifications}
                        </span>
                      ) : null}
                    </div>
                    <div className="mt-3 space-y-2">
                      {allNotifications.slice(0, 3).map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => handleOpenNotification(item)}
                          className="block w-full rounded-[12px] bg-[rgba(var(--clr-on-surface),0.03)] px-3 py-3 text-left"
                        >
                          <p className="text-body-medium text-on-surface">{item.title}</p>
                          <p className="mt-1 text-body-small text-on-surface-var">{item.message}</p>
                        </button>
                      ))}
                      {!allNotifications.length ? (
                        <p className="text-body-medium text-on-surface-var">No admin alerts yet.</p>
                      ) : null}
                    </div>
                  </div>

                  <div className="space-y-1">
                    {navItems.map((item) => (
                      <MobileNavButton
                        key={item.key}
                        label={item.label}
                        icon={item.icon}
                        active={section === item.key}
                        onClick={() => handleMobileNavigate(item.to)}
                      />
                    ))}
                  </div>
                </div>

                <div className="border-t border-[rgba(var(--clr-outline-variant),0.4)] px-3 py-3">
                  <div className="mb-3 rounded-[12px] bg-[rgba(var(--clr-on-surface),0.03)] px-3 py-3">
                    <div className="flex items-center gap-3">
                      <AvatarThumb68
                        src={avatarSrc}
                        alt="Admin avatar"
                        shape="circle"
                        role="doctor"
                        fallbackSrc={FALLBACK_AVATAR}
                        className="aspect-square"
                        style={{ width: 40, height: 40 }}
                        imgClassName={avatarFailed ? "object-contain p-[2px]" : "object-cover p-0"}
                      />
                      <div className="min-w-0">
                        <p className="truncate text-body-medium text-on-surface">{fullName}</p>
                        <p className="truncate text-body-small text-on-surface-var">{email || "Admin account"}</p>
                        <p className="text-body-small text-on-surface-var">Avatar follows your AfriMed account photo when available.</p>
                      </div>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => handleMobileNavigate("/admin/account")}
                    className="mb-3 flex w-full items-center gap-3 rounded-[12px] px-4 py-3 text-left text-body-large text-on-surface hover:bg-[rgba(var(--clr-on-surface),0.06)]"
                  >
                    <UserRound className="h-5 w-5 text-on-surface-var" aria-hidden />
                    <span className="flex-1">My account</span>
                    <Upload className="h-5 w-5 text-on-surface-var" aria-hidden />
                  </button>

                  <div className="mb-3 rounded-[12px] border border-[rgba(var(--clr-outline-variant),0.4)] bg-[rgb(var(--clr-surface-cont-highest))] px-3 py-3">
                    <p className="text-body-medium text-on-surface-var">Theme</p>
                    <div className="mt-2 space-y-1">
                      {THEME_OPTIONS.map((option) => (
                        <ThemeOptionButton
                          key={option.value}
                          active={theme === option.value}
                          label={option.label}
                          icon={option.icon}
                          onClick={() => handleThemeSelect(option.value)}
                        />
                      ))}
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => setLogoutOpen(true)}
                    className="flex w-full items-center gap-3 rounded-[12px] px-4 py-3 text-left text-body-large text-on-surface hover:bg-[rgba(var(--clr-on-surface),0.06)]"
                  >
                    <LogOut className="h-5 w-5 text-on-surface-var" aria-hidden />
                    <span>{loggingOut ? "Signing out…" : "Sign out"}</span>
                  </button>
                </div>
              </aside>
            </div>,
            document.body,
          )
        : null}

      {notificationsOpen && typeof document !== "undefined"
        ? createPortal(
            <div className="md:hidden">
              <div
                className="fixed inset-0 z-[88] bg-[rgba(15,23,42,0.42)] backdrop-blur-[6px]"
                onClick={() => setNotificationsOpen(false)}
              />
              <div className="fixed inset-x-0 bottom-0 z-[89] rounded-t-[24px] bg-[rgb(var(--clr-surface-cont-highest))] shadow-[0px_-12px_24px_-10px_rgba(29,79,215,0.10),0px_-6px_12px_-4px_rgba(26,27,35,0.08)]">
                <div className="border-b border-[rgba(var(--clr-outline-variant),0.4)] px-4 py-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-title-medium text-on-surface">Notifications</p>
                      <p className="mt-1 text-body-small text-on-surface-var">
                        Admin alerts and operational notices will appear here.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setNotificationsOpen(false)}
                      className="grid h-10 w-10 place-items-center rounded-[12px] hover:bg-[rgba(var(--clr-on-surface),0.06)]"
                      aria-label="Close notifications"
                    >
                      <X className="h-4 w-4 text-on-surface-var" aria-hidden />
                    </button>
                  </div>
                </div>
                <div className="max-h-[60vh] overflow-y-auto px-4 py-4">
                  {allNotifications.length ? (
                    <div className="space-y-2">
                      {allNotifications.map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => handleOpenNotification(item)}
                          className="block w-full rounded-[12px] bg-[rgba(var(--clr-on-surface),0.03)] px-3 py-3 text-left"
                        >
                          <p className="text-body-medium text-on-surface">{item.title}</p>
                          <p className="mt-1 text-body-small text-on-surface-var">{item.message}</p>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-body-medium text-on-surface-var">No admin alerts yet.</p>
                  )}
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}

      {logoutOpen &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="fixed inset-0 z-[220] flex items-center justify-center bg-[rgba(15,23,42,0.35)] backdrop-blur-sm"
            role="dialog"
            aria-modal="true"
            onClick={() => (loggingOut ? null : setLogoutOpen(false))}
          >
            <div
              className="w-[420px] max-w-[calc(100vw-32px)] overflow-hidden rounded-2xl bg-[rgb(var(--clr-surface-cont-highest))] shadow-[0_18px_45px_rgba(15,23,42,0.18)]"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="p-6">
                <div className="mx-auto flex w-full max-w-[320px] items-start justify-center gap-3">
                  <LogOut className="mt-0.5 h-6 w-6 shrink-0 text-[rgb(var(--schemes-critical))]" aria-hidden />
                  <div className="min-w-0">
                    <div className="text-title-medium text-on-surface">Are you sure you want to log out?</div>
                    <div className="mt-1 text-body-medium text-on-surface-var">
                      You’ll need to sign in again to continue.
                    </div>
                  </div>
                </div>

                <div className="mt-5 flex items-center justify-center gap-3">
                  <button
                    type="button"
                    className="flex-1 rounded-xl px-4 py-3 text-label-large disabled:opacity-60"
                    style={{
                      background: "var(--State-Layers-Primary-Opacity-08,rgba(29,79,215,0.08))",
                      color: "var(--Schemes-Primary,#1D4FD7)",
                    }}
                    disabled={loggingOut}
                    onClick={() => setLogoutOpen(false)}
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    className="flex-1 rounded-xl px-4 py-3 text-label-large disabled:opacity-60"
                    style={{
                      background: "var(--State-Layers-Critical-Opacity-08,rgba(248,83,58,0.08))",
                      color: "var(--Schemes-Critical,#F8533A)",
                    }}
                    disabled={loggingOut}
                    onClick={handleLogout}
                  >
                    {loggingOut ? "Logging out…" : "Log out"}
                  </button>
                </div>
              </div>

              <div className="h-px bg-[rgba(15,23,42,0.06)]" />
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
